import React, { type FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Tooltip from '@atlaskit/tooltip';
import { Box } from '@atlaskit/primitives/compiled';

import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const styles = cssMap({
	tooltipWrapper: {
		minWidth: '0px',
	},
});

const i18n = defineMessages({
	addedTooltip: {
		id: 'inline-comments-common.date-tooltip.added',
		defaultMessage: 'Added by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of author and relative temporal context for when comment was added',
	},
});

type MaybeTooltipProps = {
	children: React.ReactNode;
	date?: string | null;
	name: string;
};

export const MaybeTooltip: FC<MaybeTooltipProps> = ({ children, date, name }) => {
	const intl = useIntl();
	const { formatMessage } = intl;

	const { timeZone: userTimeZone, tenantTimeZone } = useSessionData();
	const timeZone = userTimeZone || tenantTimeZone || undefined;

	return date && expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
		<Box xcss={styles.tooltipWrapper}>
			<Tooltip
				content={formatMessage(i18n.addedTooltip, {
					username: name,
					date: intl.formatDate(getValidDate(date), {
						month: 'short',
						day: 'numeric',
						year: 'numeric',
						timeZone,
					}),
					timeStamp: intl.formatTime(getValidDate(date), {
						hour: 'numeric',
						minute: 'numeric',
						timeZone,
					}),
				})}
			>
				{children}
			</Tooltip>
		</Box>
	) : (
		<>{children}</>
	);
};
